import { App, watch, watchEffect } from "vue";
import * as Sentry from "@sentry/vue";
import { router } from "../../router";
import { pinia, useAuth } from "../../store";
import { isDevelopmentEnvironment } from "../../utils/environment";
import { scrubUiBreadcrumbPayload } from "./sentryUiBreadcrumbScrub";

// ENG-15784 disabled Sentry in Storybook
const isStorybook = typeof window !== "undefined" && (window as any).__STORYBOOK_PREVIEW__ === true;

export default function () {
  if (isStorybook) return; // <-- skip in Storybook

  return {
    install(app: App) {
      Sentry.init({
        app,
        // ENG-21756 No DSN on development builds: local machines were reporting to the production
        // project. Sentry still initialises so its Vue/router instrumentation stays in place.
        dsn: isDevelopmentEnvironment(import.meta.env.APP_ENVIRONMENT)
          ? undefined
          : import.meta.env.SENTRY_DSN,
        release: import.meta.env.RELEASE_NUMBER,
        environment: import.meta.env.APP_ENVIRONMENT,

        integrations: [
          Sentry.browserTracingIntegration({
            router,
          }),

          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
            // ENG-20571: keep modal headers/titles legible in replays so we
            // know which screen the user was on when they sent feedback.
            unmask: [".modal--header-title"],
            // ENG-22245: rage/dead-click detection reads a hardcoded attribute
            // allowlist off the clicked element (aria-label, title, alt, name,
            // etc. included) and its own masking doesn't cover the raw
            // "Selector Path" breadcrumb message — see
            // sentryUiBreadcrumbScrub.ts for the full explanation.
            beforeAddRecordingEvent(event) {
              const data = event.data as { tag?: unknown; payload?: unknown } | undefined;
              if (data?.tag === "breadcrumb") {
                scrubUiBreadcrumbPayload(data.payload);
              }
              return event;
            },
          }),

          Sentry.feedbackIntegration({
            // ENG-20571: we trigger captureFeedback() programmatically from the
            // user menu, so don't inject the floating Sentry widget into the DOM.
            autoInject: false,
            colorScheme: "system",
            isNameRequired: true,
            isEmailRequired: true,
          }),
        ],

        // ENG-21349 0.2 → 0.05: cut span volume (~4.35M/30d) that was eating the org's shared budget.
        // ENG-21639 0.05 → 0.001: cut non-error span volume (was ~5.6M/30d, top consumer org-wide).
        tracesSampleRate: 0.001,

        // Disabled because NHS Credentials Manager
        // rejects sentry-trace/baggage CORS preflight headers
        tracePropagationTargets: [],
        // ENG-21639 1.0 → 0.5: staff-app hits a high error volume, so halve
        // error-session replay recording to save budget (matches patient-portal).
        replaysOnErrorSampleRate: 0.5,
        // ENG-21349 0.9 → 0.01: stop recording uneventful sessions. On-error (above) and
        // on-demand feedback replays (UserMenu.vue, ENG-20571) are unaffected.
        // ENG-21639 0.01 → 0: preserve replay budget for error sessions only.
        replaysSessionSampleRate: 0,

        // ENG-20571: attach a synthetic stack to non-exception events so user
        // feedback (which is a `type: 'feedback'` event, not an exception)
        // includes the code path that triggered it.
        attachStacktrace: true,

        // ENG-20636 Drop known-noise errors before they hit the queue.
        // Sentry does a substring / regex match on the event message —
        // each entry below matches the start of a real noisy issue group
        // in the staff-app project (verified via Sentry MCP, 2026-05-21).
        ignoreErrors: [
          // ENG-20636 Deliberately NOT filtering "Non-Error promise rejection
          // captured" yet — ENG-20739 fixes a regression that surfaces as
          // exactly that GlobalHandlers-formatted message. Keep this entry
          // commented out until ENG-20739 verifies in prod, then re-add.
          // "Non-Error promise rejection captured",
          // Unhandled rejections we don't / can't act on
          "Object Not Found Matching Id", // Outlook "Search by Object" browser extension

          // Network / user-initiated aborts — not bugs
          "Network Error", // axios when offline / preflight blocked
          "Request failed with status code 400", // client validation, expected
          "AxiosError: Request aborted",
          "AxiosError: timeout exceeded",

          // WebUSB / Zebra printer integration — environmental, not actionable.
          // Deliberately NOT filtering the generic DOMException names
          // (NotAllowedError, PermissionDeniedError, OverconstrainedError) —
          // they're shared with camera/mic/clipboard and would mask real
          // media-device bugs in video calls.
          "NotReadableError", // USB device busy / disconnected
          "SecurityError: Failed to execute 'open' on 'USBDevice'",
          "Error fetching device.",

          // Third-party library / framework warnings
          "AG Grid: cannot get grid to draw rows",
          "InvalidStateError: Failed to execute 'setParameters'", // WebRTC race during video calls
        ],

        // ENG-20636 Axios cancellations used to be filtered by a manual
        // window.onunhandledrejection patch (since removed) — keep that
        // filter here so the default GlobalHandlers integration still drops
        // them. `__CANCEL__` is the internal axios marker that `axios.isCancel`
        // checks for; using it directly avoids importing axios in this file.
        beforeSend(event, hint) {
          const err = hint?.originalException;
          if (err && typeof err === "object" && (err as { __CANCEL__?: boolean }).__CANCEL__) {
            return null;
          }
          return event;
        },

        // ENG-22245: same scrub as replayIntegration's beforeAddRecordingEvent
        // above, for UI breadcrumbs attached directly to captured error events
        // rather than the replay recording stream.
        beforeBreadcrumb(breadcrumb) {
          scrubUiBreadcrumbPayload(breadcrumb);
          return breadcrumb;
        },

        // ENG-21349 Drop the unconsumed Vue root render/mount spans (~780k/30d); vueIntegration
        // emits them unconditionally while tracing is on (trackComponents only gates child spans).
        beforeSendTransaction(event) {
          if (event.spans) {
            event.spans = event.spans.filter(
              (s) => s.op !== "ui.vue.render" && s.op !== "ui.vue.mount",
            );
          }
          return event;
        },
      });

      app.provide("SENTRY", Sentry);

      const auth = useAuth(pinia);

      window.captureException = Sentry.captureException;

      watchEffect(() => {
        if (auth.user) {
          Sentry.setUser({
            id: auth.user.userId,
            username: auth.user.username,
            email: auth.user.username,
          });

          Sentry.setTag("username", auth.user.username);
        } else {
          Sentry.setUser(null);
        }
      });

      watch(
        () => auth.selectedTenant,
        (t) => Sentry.setTag("tenant", t),
        { immediate: true },
      );

      watchEffect(() => {
        const orgName = app.config.globalProperties.$settings?.localOrganisation?.name;

        if (orgName) {
          Sentry.setTag("orgName", orgName);
        }
      });

      app.config.errorHandler = (err, vm, info) => {
        if (err) {
          Sentry.setTag("info", info);
          Sentry.captureException(err);

          console.error(err);
        }

        return false;
      };

      // ENG-20636 Removed in this change:
      //  • app.config.warnHandler that captured every Vue runtime warning
      //    (~2.8k <unknown> events / 30d — wrong tool; warnings ≠ errors).
      //  • window.addEventListener("error", …) which double-captured every
      //    uncaught error. @sentry/vue v10's GlobalHandlers integration
      //    already covers window.error.
      //  • setTimeout-patched window.onunhandledrejection + the manual
      //    "unhandledrejection" listener. GlobalHandlers covers that too;
      //    the axios.isCancel filter moved into `beforeSend` above.

      router.onError((error) => {
        Sentry.captureException(error);
      });
    },
  };
}
